import React, { useState, useRef } from 'react';
import { MdDelete, MdOutlineCheck } from 'react-icons/md';
import VisDiv from '../frontend_components/VisDiv';
import {AiFillCamera} from 'react-icons/ai'
import { GrGallery } from "react-icons/gr";
import { MdInsertPhoto } from "react-icons/md";
import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  convertToPixelCrop,
} from 'react-image-crop';
// Canvas preview helper function


/**
 * Canvas preview helper function
 * #pictures
 * @param {HTMLImageElement} image - The image to be cropped
 * @param {HTMLCanvasElement} canvas - The canvas to render the cropped image on
 * @param {PixelCrop} crop - The crop object containing the crop's position and size
 * @param {number} scale - The scale of the image (default is 1)
 * @param {number} rotate - The rotation of the image in degrees (default is 0)
 * @returns {Promise<void>}
 * @throws {Error} If no 2d context is available
 */
function canvasPreview(
  image,
  canvas,
  crop,
  scale = 1,
  rotate = 0
) {
  const ctx = canvas.getContext('2d', {
    // Enable alpha channel for better quality
    alpha: true,
    // Use high color depth
    colorSpace: 'display-p3',
    // Prevent premultiplied alpha
    willReadFrequently: true
  });
  
  if (!ctx) {
    throw new Error('No 2d context');
  }

  // Calculate dimensions preserving original image quality
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const pixelRatio = Math.max(window.devicePixelRatio, 2); // Ensure high DPI support

  // Set canvas dimensions with high resolution
  canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
  canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

  // Clear previous content
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // Configure context for high quality
  ctx.scale(pixelRatio, pixelRatio);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  
  // Set composition mode for better quality
  ctx.globalCompositeOperation = 'source-over';

  const cropX = crop.x * scaleX;
  const cropY = crop.y * scaleY;

  const rotateRads = rotate * Math.PI / 180;
  const centerX = image.naturalWidth / 2;
  const centerY = image.naturalHeight / 2;

  ctx.save();

  // Move the crop origin to the canvas origin (0,0)
  ctx.translate(-cropX, -cropY);
  // Move the origin to the center of the original position
  ctx.translate(centerX, centerY);
  // Rotate around the origin
  ctx.rotate(rotateRads);
  // Move the center of the image to the origin (0,0)
  ctx.translate(-centerX, -centerY);
  // Draw the image with high quality settings
  ctx.drawImage(
    image,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight,
  );

  ctx.restore();
}
import { useDebounceEffect } from './useDebounceEffect';
import 'react-image-crop/dist/ReactCrop.css';
import { convertBase64, resizeBase64 } from '../../lib/utils';

// This is to demonstrate how to make and center a % aspect crop
// which is a bit trickier so we use some helper functions.
function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

/**
 * ImageCrop is a React component for cropping images.
 * It provides a preview of the cropped image and allows users to download the cropped image.
 * It also provides a way to cancel the cropping process.
 * #pictures
 * @param {Object} props - The properties of the component.
 * @param {number} props.iconSize - The size of the crop buttons.
 * @param {number} props.customWidth - The custom width of the image.
 * @param {boolean} props.download - Whether to show the download button.
 * @param {boolean} props.hidePreview - Whether to hide the preview.
 * @param {function} props.isCropping - A function to be called when the cropping process starts.
 * @param {function} props.saveFn - A function to be called when the cropped image is downloaded.
 */
export default function ImageCrop({iconSize = 40, customWidth, download, hidePreview, isCropping = ()=>{}, saveFn = ()=>{} }) {
    
  const [imgSrc, setImgSrc] = useState('');
  const previewCanvasRef = useRef(undefined);
  const imgRef = useRef(undefined);
  const hiddenAnchorRef = useRef(undefined);
  const blobUrlRef = useRef('');
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [aspect, setAspect] = useState(1 / 1);
  const hiddenFileInput = React.useRef(undefined);

  const hidePreviewStyle = hidePreview ? {display: "none"} : {}

  const cancel = () => {
    setImgSrc(undefined)
    setCrop(undefined)
    setCompletedCrop(undefined)
  }

  const handleClick = event => {
    hiddenFileInput.current.click();
  };

  function onSelectFile(e) {
    if (e.target.files && e.target.files.length > 0) {
      setCrop(undefined); // Makes crop preview update between images.
      
      const reader = new FileReader();
      reader.addEventListener('load', () =>
        setImgSrc(reader.result?.toString() || '')
      );
      reader.readAsDataURL(e.target.files[0]);
      console.log("dwldjwelkidfjk file size",e.target.files[0]?.size)
      isCropping(true)
    }
  }

  function onImageLoad(e) {
    if (aspect) {
      const { width, height } = e.currentTarget;
      setCrop(centerAspectCrop(width, height, aspect));
    }
  }

  async function onDownloadCropClick() {
    const image = imgRef.current;
    const previewCanvas = previewCanvasRef.current;
    if (!image || !previewCanvas || !completedCrop) {
      throw new Error('Crop canvas does not exist');
    }

    // This will size relative to the uploaded image size.
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    // Create a temporary canvas for iOS compatibility
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = completedCrop.width * scaleX;
    tempCanvas.height = completedCrop.height * scaleY;
    
    const ctx = tempCanvas.getContext('2d', {
      alpha: true, // Enable alpha channel to preserve transparency
      willReadFrequently: true
    });
    if (!ctx) {
      throw new Error('No 2d context');
    }

    // Ensure proper rendering on iOS
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    
    // Clear the canvas with a transparent background
    ctx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);

    ctx.drawImage(
      previewCanvas,
      0,
      0,
      previewCanvas.width,
      previewCanvas.height,
      0,
      0,
      tempCanvas.width,
      tempCanvas.height
    );
    
    let blob;
    
    try {
      // First ensure we have valid canvas data
      const imageData = ctx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
      if (!imageData.data.some?.(channel => channel !== 0)) {
        throw new Error('Canvas contains no image data');
      }

      // For iOS compatibility, draw to an intermediate canvas
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = tempCanvas.width;
      finalCanvas.height = tempCanvas.height;
      const finalCtx = finalCanvas.getContext('2d', {
        alpha: true, // Enable alpha channel to preserve transparency
        willReadFrequently: true
      });
      
      if (!finalCtx) {
        throw new Error('Failed to get 2d context for final canvas');
      }

      // Draw with high quality settings
      finalCtx.imageSmoothingEnabled = true;
      finalCtx.imageSmoothingQuality = 'high';
      
      // Clear the canvas with a transparent background
      finalCtx.clearRect(0, 0, finalCanvas.width, finalCanvas.height);
      finalCtx.drawImage(tempCanvas, 0, 0);

      // Handle image conversion and quality
      const getImageBlob = () => new Promise((resolve, reject) => {
        // Always use PNG to preserve transparency
        finalCanvas.toBlob(
          (pngBlob) => {
            if (pngBlob && pngBlob.size > 0) {
              console.log('Created PNG blob:', pngBlob.size);
              resolve(pngBlob);
            } else {
              // Fallback to JPEG with high quality only if PNG fails
              finalCanvas.toBlob(
                (jpegBlob) => {
                  if (jpegBlob && jpegBlob.size > 0) {
                    console.log('Created JPEG blob (transparency will be lost):', jpegBlob.size);
                    resolve(jpegBlob);
                  } else {
                    reject(new Error('Failed to create image blob'));
                  }
                },
                'image/jpeg',
                1.0  // Maximum quality
              );
            }
          },
          'image/png'
        );
      });

      blob = await getImageBlob();
      
      // Verify blob content
      if (blob.size === 0) {
        throw new Error('Generated blob is empty');
      }

      // Convert to base64 with additional error checking
      const base64Data = await convertBase64(blob);
      if (!base64Data || base64Data.length === 0) {
        throw new Error('Failed to convert image to base64');
      }

      // Verify base64 data contains image information
      if (!base64Data.startsWith('data:image/')) {
        throw new Error('Invalid image data format');
      }

      console.log('Successfully processed image:', {
        blobSize: blob.size,
        base64Length: base64Data.length
      });
      
      saveFn(base64Data);
      cancel();

    } catch (error) {
      console.error('Error in image processing:', error);
      // Re-throw to prevent silent failures
      throw error;
    }
/*     convertBase64(blob)
      ?.then((data)=>{
      resizeBase64(data)?.then((newData)=>{
       // console.log("NEW PIC", data)
        //console.log("PIC SIZE", base64Size(newData))
        saveFn(newData)
        cancel()
    })})
 */

    if (blob && blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
    }
    
    if (blob) {
      blobUrlRef.current = URL?.createObjectURL(blob);

      if (hiddenAnchorRef.current) {
        hiddenAnchorRef.current.href = blobUrlRef.current;
        hiddenAnchorRef.current.click();
      }
    }
  }

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef?.current &&
        previewCanvasRef?.current
      ) {
        // We use canvasPreview as it's much faster than imgPreview.
        canvasPreview(
          imgRef?.current,
          previewCanvasRef?.current,
          completedCrop,
          scale,
          rotate
        );
      }
    },
    100,
    [completedCrop, scale, rotate]
  );

  const testFunction = () => {
    console.log("TEST CROP", crop, "TEST IMAGE", imgSrc)
  }

  const imageWidth = customWidth ? { width: "100%", maxWidth: customWidth } : {}


  return (
    <div className="App">
       {/* <button style={{position:"absolute"}} onClick={testFunction}>TEST BUTTON CROP</button> */}
      <VisDiv rules={[!imgSrc]} className="Crop-Controls">
      <button onClick={handleClick} style={{backgroundColor:"green",   width: `${iconSize}px`, height: `${iconSize}px`}} className='round-red-button'>
        <MdInsertPhoto
        size={iconSize/2}
        color={"white"}
        />
      </button>
        <input 
          type="file" 
          accept="image/*" 
          onChange={onSelectFile} 
          name="upload-image"
          ref={hiddenFileInput}
          style={{display:"none"}}
          className="insert-photo-button"
        
        />
      </VisDiv>
      <VisDiv rules={[!!imgSrc]}>
      <VisDiv rules={[!download, imgSrc]} style={{display:"flex", width:"100%", justifyContent:"space-between", marginTop:"10px", marginBottom: "10px"}}>
          <button onClick={onDownloadCropClick} style={{ backgroundColor:"green", width:"100px", height:"100px"}} className='round-red-button'>
              <MdOutlineCheck
              size={45}
              color={"white"}
              />
          </button>
          <button onClick={cancel} style={{width:"100px", height:"100px"}} className='round-red-button'>
              <MdDelete
              size={45}
              color={"white"}
              />
          </button>
       
      </VisDiv>
      
        <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={aspect}
          >
            <img
              ref={imgRef}
              alt="Crop me"
              src={imgSrc}
              style={{...imageWidth, transform: `scale(${scale}) rotate(${rotate}deg)` }}
              onLoad={onImageLoad}
            />
          </ReactCrop>
      </VisDiv>
      <VisDiv rules={[!!completedCrop]}>
      <>
          <div>
            <canvas
              ref={previewCanvasRef}
              style={{
                border: '1px solid black',
                objectFit: 'contain',
                width: completedCrop?.width,
                height: completedCrop?.height,
                ...hidePreviewStyle
              }}
            />
          </div>

          <VisDiv rules={[download]}>
            <button onClick={onDownloadCropClick}>Download Crop</button>
            <a
              href="#hidden"
              ref={hiddenAnchorRef}
              download
              style={{
                position: 'absolute',
                top: '-200vh',
                visibility: 'hidden',
              }}
            >
              Hidden download
            </a>
          </VisDiv>
        </>
      </VisDiv>

    </div>
  );
}
